<script>
    import Recommendation from "./Recommendation.svelte";
    import LazyLoad from "vanilla-lazyload";
    import { onMount, setContext } from "svelte";
    import { generateRandomId } from "../utils/IdentifierGenerator";
    import scrollTracker from "../tracking/scrollTracker";
    import { calculateTileDimension } from "../utils/tileDimensionCalculator";
    import { key as cinemaTileDimensionCtxKey } from "../context/cinemaTileDimension";
    import { key as featureStateCtxKey } from "../context/featureState";
    import { key as contentDresonRuleCtxKey } from "../context/contentDresonRule";
    import { emitMiniActionEvent } from "../tracking/eventEmitter.js";
    import viewTracker from "../tracking/viewTracker.js";

    /**
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 *
 */

    /*                */
    let {
        headline = null,
        subHeadline = null,
        recommendations,
        tracking,
        featureIndex,
        cinemaIndex = null,
        cinemaMeta,
        additionalClickTracking,
        site,
        sourceVariationId = undefined,
        cinemaInjectionContainerWidth,
        featuresState = {},
        wishlist_class = false,
        additionalNextPiTrackingLabels = null,
        additionalTrackingLabels = null,
        topPadding = 16,
        bottomPadding = 24,
        contentDresonRule = null,
        showTopInfo = false,
        trackingEventMergeId = null,
        loadedTrackingActionName = null,
        openedCallback = null,
        removeShoeSizeFilterCallback = null,
        personalisedUser = false,
        cinemaContainerMounted = Promise.resolve(),
    } = $props();

    const initialVisibleTiles = 12;
    let renderNodeTiles = $state(initialVisibleTiles);

    let { width, height, numberOfVisibleTiles, minimumHeight, minimumWidth } = $derived(
        calculateTileDimension(cinemaInjectionContainerWidth)
    );
    let tileDimensions = $state({
        get width() {
            return width;
        },
        get height() {
            return height;
        },
        get numberOfVisibleTiles() {
            return numberOfVisibleTiles;
        },
        get minimumWidth() {
            return minimumWidth;
        },
        get minimumHeight() {
            return minimumHeight;
        },
    });
    setContext(cinemaTileDimensionCtxKey, tileDimensions);
    setContext(featureStateCtxKey, featuresState);
    setContext(contentDresonRuleCtxKey, contentDresonRule);

    const lazyLoadConfig = {
        unobserve_entered: true,
        threshold: 0,
        callback_loaded: (el) => {
            el.parentElement.nextElementSibling.firstElementChild.classList.remove("p_loader250");
        },
        callback_error: (el) => {
            el.parentElement.nextElementSibling.firstElementChild.classList.remove("p_loader250");
        },
    };

    let cinemaFeature;
    const cinemaId = `reco_cinema__${generateRandomId(16)}`;
    let visibilityPreviousActionAlreadyTracked = false;

    const trackView = (recommendations, numberOfVisibleTiles) => {
        const recommendationsTracking = recommendations.slice(0, numberOfVisibleTiles + 1).map((recommendation) => recommendation.tracking);

        if (!openedCallback) {
            viewTracker(
                tracking,
                recommendationsTracking,
                featureIndex,
                cinemaIndex,
                trackingEventMergeId,
                additionalTrackingLabels,
                loadedTrackingActionName
            );
        }
        openedCallback?.(numberOfVisibleTiles);
    };

    const trackVisibilityVerticalScroll = (recommendations, numberOfVisibleTiles) => {
        const { id: cinemaFeatureId } = tracking;
        const tilesFeatureIds = recommendations.slice(0, numberOfVisibleTiles + 1).map((recommendation) => recommendation.tracking.id);
        const featureIds = [cinemaFeatureId, ...tilesFeatureIds];
        emitMiniActionEvent(featureIds);
    };

    const trackVisibilityHorizontalScroll = (direction, tilesFeatureIds = []) => {
        const visibleRecos = recommendations.filter((reco) => tilesFeatureIds.includes(reco.tracking?.id));
        const recommendationsTracking = visibleRecos.map(({ tracking }) => tracking);
        const recommendationsMeta = visibleRecos.map(({ meta }) => meta);
        scrollTracker(
            tracking,
            recommendationsTracking,
            featureIndex,
            cinemaIndex,
            direction,
            cinemaMeta,
            recommendationsMeta,
            sourceVariationId,
            additionalTrackingLabels
        );
    };

    cinemaContainerMounted.then(() => {
        const visibleTiles = parseInt(window.getComputedStyle(document.getElementById(cinemaId)).getPropertyValue("--items-per-page") || 2);
        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    if (renderNodeTiles < recommendations.length) {
                        requestAnimationFrame(() => {
                            renderNodeTiles = recommendations.length;
                        });
                    }
                    trackVisibilityVerticalScroll(recommendations, visibleTiles);
                    observer.unobserve(entry.target);
                }
            });
        });
        observer.observe(cinemaFeature);
        trackView(recommendations, visibleTiles);
    });

    const lazyLoadImages = () => {
        if ("loading" in HTMLImageElement.prototype) {
            const images = document.querySelectorAll(".reco_cinema__image .lazy");
            images.forEach((img) => {
                img.parentElement.nextElementSibling.firstElementChild.classList.remove("p_loader250");
                img.src = img.dataset.src;
            });
        } else {
            const images = document.querySelectorAll(".reco_cinema__image .lazy");
            const lazyLoader = new LazyLoad(lazyLoadConfig, images);
            lazyLoader.update();
        }
    };

    onMount(() => {
        lazyLoadImages();

        const cinema = document.querySelector(`#${cinemaId}`);

        cinema.addEventListener("oc-stage-scroll-completed", (event) => {
            if (event.target.id !== cinemaId) return;
            const tilesFeatureIds = event.detail.visibleItems.indices.map((index) => recommendations[index]?.tracking?.id);
            const direction = event.detail.scrollDirection === "right" ? "next" : "previous";
            if (direction === "previous") {
                if (!visibilityPreviousActionAlreadyTracked) {
                    trackVisibilityHorizontalScroll(direction);
                    visibilityPreviousActionAlreadyTracked = true;
                }
            } else {
                trackVisibilityHorizontalScroll(direction, tilesFeatureIds);
            }
        });
    });
</script>

<div bind:this={cinemaFeature} class="reco_cinema {site}" style="--bottomPadding: {bottomPadding}; --topPadding: {topPadding}">
    {#if headline !== null}
        <h2 class="reco_cinema__headline oc-headline-100 oc-headline-200--l" class:oc-pb-50={subHeadline} class:oc-pb-100={!subHeadline}>
            {headline}
        </h2>
        {#if subHeadline}
            <div class="oc-ml-100 oc-mb-50">
                <div class="oc-copy-75">
                    {subHeadline}
                    <br />
                    <!-- svelte-ignore  a11y_click_events_have_key_events -->
                    <!-- svelte-ignore  a11y_no_static_element_interactions -->
                    <oc-link-v2 class="oc-copy-75" data-testid="remove-shoe-filter" onclick={removeShoeSizeFilterCallback} as-button
                        >Auswahl aufheben
                    </oc-link-v2>
                </div>
            </div>
        {/if}
    {/if}
    <oc-cinema-v1
        id={cinemaId}
        gap="8px"
        resizing="cut"
        arrow-button-position="{tileDimensions.height / 2}px"
        class="reco_cinema__carousel"
        stage-spacing="16px"
    >
        {#each recommendations as recommendation, i}
            {#if i < renderNodeTiles}
                <div class="reco_cinema_tile_wrapper">
                    <Recommendation
                        {recommendation}
                        lazyLoad={i > 4}
                        cinemaTracking={tracking}
                        {featureIndex}
                        {cinemaIndex}
                        additionalCinemaClickTracking={additionalClickTracking}
                        {cinemaMeta}
                        {featuresState}
                        {cinemaId}
                        {additionalNextPiTrackingLabels}
                        {additionalTrackingLabels}
                        {wishlist_class}
                        {showTopInfo}
                        {personalisedUser}
                    />
                </div>
            {/if}
        {/each}
    </oc-cinema-v1>
</div>

<style lang="scss">
    .reco_cinema {
        height: 100%;
        width: 100%;
        background-color: transparent;
        line-height: normal;
        padding: calc(var(--topPadding) * 1px) 0 calc(var(--bottomPadding) * 1px);
        container-type: inline-size;
    }

    .reco_cinema__headline {
        padding-bottom: 16px;
        margin-left: 16px;
    }

    .reco_cinema__tiles {
        height: 100%;
        margin: 0;
        padding: 0;
        white-space: nowrap;
        font-size: 0; /*                     */
        word-spacing: 0;
    }

    .reco_cinema__carousel {
        font-size: 0px;
        --items-per-page: 2;
    }

    @container (width > 767px) {
        .reco_cinema__carousel {
            --items-per-page: 5;
        }
    }

    @container ((width > 636px) and (width <= 767px)) {
        .reco_cinema__carousel {
            --items-per-page: 4;
        }
    }

    @container ((width >= 497px) and (width <= 636px)) {
        .reco_cinema__carousel {
            --items-per-page: 3;
        }
    }

    @container (width < 497px) {
        .reco_cinema__carousel {
            --items-per-page: 2;
        }
    }

    .reco_basketLayer {
        padding: 0;
    }
</style>
